import { v } from 'convex/values'

export const TITLE_MAX = 60
export const TITLE_REFRESH_AT = 6
export const TITLE_REQUEUE_MS = 2 * 60_000
export const TITLE_BACKFILL_PER_CALL = 5

export const titleSourceValidator = v.union(v.literal('auto'), v.literal('user'))

export const COACH_CHAT_META = {
  titleSource: v.optional(titleSourceValidator),
  // Turn count when the auto title was written; the refresh rule reads it.
  titledAt: v.optional(v.number()),
  titleGeneric: v.optional(v.boolean()),
  titleQueuedAt: v.optional(v.number()),
  pinned: v.optional(v.boolean()),
}

export type TitleSource = 'auto' | 'user'

export type TitleMeta = {
  titleSource?: TitleSource
  titledAt?: number
  titleGeneric?: boolean
  titleQueuedAt?: number
}

export type TurnCounts = { user: number; total: number }

// First auto title once the coach has answered; one retitle at TITLE_REFRESH_AT
// turns if the first was generic. A user rename always wins.
export function titleDue(meta: TitleMeta, turns: TurnCounts, now: number): boolean {
  if (meta.titleSource === 'user') return false
  if (meta.titleQueuedAt && now - meta.titleQueuedAt < TITLE_REQUEUE_MS) return false
  if (turns.user < 1 || turns.total - turns.user < 1) return false
  if (meta.titleSource !== 'auto') return true
  return (
    !!meta.titleGeneric &&
    (meta.titledAt ?? 0) < TITLE_REFRESH_AT &&
    turns.total >= TITLE_REFRESH_AT
  )
}

export function countTurns(
  msgs: { role: 'user' | 'assistant'; content: string; isOpener?: boolean }[],
): TurnCounts {
  const real = msgs.filter((m) => m.content.trim() && !m.isOpener)
  return { user: real.filter((m) => m.role === 'user').length, total: real.length }
}

export function cleanTitle(raw: string): string {
  const t = raw
    .replace(/\p{Extended_Pictographic}|️|‍/gu, '')
    .replace(/^(title\s*:\s*)/i, '')
    .replace(/["“”„«»「」『』'`*_#]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.。!！?？:：;,，、-]+$/u, '')
    .trim()
  if (!t) return ''
  const capped = t.length > TITLE_MAX ? `${t.slice(0, TITLE_MAX - 1).trimEnd()}…` : t
  return capped.charAt(0).toLocaleUpperCase() + capped.slice(1)
}

export function titlePrompt(turns: { role: 'user' | 'assistant'; content: string }[]): string {
  const chat = turns
    .map((m) => `${m.role === 'user' ? 'User' : 'Coach'}: ${m.content.slice(0, 500)}`)
    .join('\n')
  return `Title this chat between a user and their goal coach, like a chat app's sidebar title.
Rules:
- 3 to 6 words, sentence case, no quotes, no emoji, no trailing punctuation.
- Write it in the language the user writes in.
- Name the specific topic (e.g. "Marathon training plan", "Morning routine slump", "Pricing the SaaS launch"), never "Chat with coach" or "Conversation".
- Set generic to true only when the chat so far is just greetings or small talk with no clear topic.

Chat:
${chat}`
}

// Text around the first matched query word, for search results.
export function matchSnippet(text: string, query: string, radius = 40): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  const lower = flat.toLowerCase()
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  let at = -1
  let len = 0
  for (const w of words) {
    const i = lower.indexOf(w)
    if (i >= 0 && (at < 0 || i < at)) {
      at = i
      len = w.length
    }
  }
  if (at < 0) return flat.length > radius * 2 ? `${flat.slice(0, radius * 2).trimEnd()}…` : flat
  const start = Math.max(0, at - radius)
  const end = Math.min(flat.length, at + len + radius)
  return `${start > 0 ? '…' : ''}${flat.slice(start, end).trim()}${end < flat.length ? '…' : ''}`
}

export type ChatGroup = 'pinned' | 'today' | 'yesterday' | 'week' | 'older'

// Pinned first, then by local-day recency; rows keep their input order.
export function groupChats<T extends { updatedAt: number; pinned: boolean }>(
  rows: T[],
  now = Date.now(),
): { group: ChatGroup; rows: T[] }[] {
  const midnight = new Date(now)
  midnight.setHours(0, 0, 0, 0)
  const today = midnight.getTime()
  const yesterday = new Date(today).setDate(new Date(today).getDate() - 1)
  const week = new Date(today).setDate(new Date(today).getDate() - 6)
  const order: ChatGroup[] = ['pinned', 'today', 'yesterday', 'week', 'older']
  const by = new Map<ChatGroup, T[]>(order.map((g) => [g, []]))
  for (const r of rows) {
    const g: ChatGroup = r.pinned
      ? 'pinned'
      : r.updatedAt >= today
        ? 'today'
        : r.updatedAt >= yesterday
          ? 'yesterday'
          : r.updatedAt >= week
            ? 'week'
            : 'older'
    by.get(g)?.push(r)
  }
  return order.map((group) => ({ group, rows: by.get(group) ?? [] })).filter((g) => g.rows.length)
}
